import React, { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import useAnalytics from "../../_hooks/useAnalytics";
import { history } from "../../_services/history";
import { processLogin, selectOrganizationByLogin, sendActivationLink } from "../../user/services/store/authentication.actions";
import {
    fireGTMTagAfterSuccessfulUserRegistration,
    fireMetaTagAfterSuccessfulUserRegistration,
} from "../../organization/myOrganization/services/store/myOrganization.actions";
import { REGISTRATION_STEPS, STEP_PROGRESS, resolvePreselectedChannels } from "./registrationConfig";
import { submitEmailRegistration, submitSsoRegistration } from "./registrationService";
import SocialProofPanel from "./components/SocialProofPanel";
import RegistrationHeader from "./components/RegistrationHeader";
import ExistingAccountPrompt from "./components/ExistingAccountPrompt";
import MfaPrompt from "./components/MfaPrompt";
import ChannelSelectionStep from "./steps/ChannelSelectionStep";
import CompanyNameStep from "./steps/CompanyNameStep";
import EmailStep from "./steps/EmailStep";
import SuccessStep from "./steps/SuccessStep";
import pathwayLogo from "../../_assets/img/registration/pathway-logo.png";
import "./registration.scss";

// Post-registration landing: the "API Verbindungen" tab of the Clients screen, where the user
// connects the selected channels' APIs (ticket #39124).
const API_CONNECTIONS_ROUTE = "/clients?tab=connections";

/**
 * Channel-first registration flow (ticket #39124, Claude Design v4).
 * Split-screen: left = 4-step flow with progress bar + "Zurück", right = social proof.
 * Route: /registration
 */
const RegistrationScreen = () => {
    const [step, setStep] = useState(REGISTRATION_STEPS.CHANNELS);

    // Registration state collected across steps.
    const [selectedChannels, setSelectedChannels] = useState(() =>
        resolvePreselectedChannels(window.location.search, document.referrer)
    );
    const [companyName, setCompanyName] = useState("");
    const [email, setEmail] = useState("");
    // "email" (4a) or "sso" (4b) — decides which success screen renders.
    const [signupMethod, setSignupMethod] = useState(null);
    // SSO token/profile from the popup — reused to log the user into the app (step 4b CTA
    // and the existing-identity path).
    const [ssoAuth, setSsoAuth] = useState(null);
    // Set to the SSO result when an existing account is detected → show the confirmation prompt.
    const [existingUserPrompt, setExistingUserPrompt] = useState(null);
    const [promptBusy, setPromptBusy] = useState(null); // "create" | "login" | null
    const [promptError, setPromptError] = useState(null);
    // 2FA challenge from the SSO popup (existing account with 2FA enabled).
    const [mfaRequired, setMfaRequired] = useState(false);
    const [mfaProvider, setMfaProvider] = useState(null);

    const dispatch = useDispatch();
    const { analytics } = useAnalytics();

    const progress = STEP_PROGRESS[step];
    // No "Zurück" on the success screen — going back and resubmitting would duplicate the registration.
    const canGoBack = step > REGISTRATION_STEPS.CHANNELS && step < REGISTRATION_STEPS.SUCCESS;

    const goBack = () => setStep((prev) => Math.max(REGISTRATION_STEPS.CHANNELS, prev - 1));

    // Log the user into the app using the Firebase session already established by the SSO popup.
    // processLogin only auto-selects/navigates for single-org users, so we always establish the
    // session and then explicitly select an organisation and navigate — otherwise a multi-org
    // existing account logs in but never leaves this screen. Org priority: the just-created org
    // (targetOrgUuid) → the user's selected org → their first available org.
    // Lands on "API Verbindungen" (Clients screen, Connections tab) per the ticket.
    const loginToApp = useCallback(
        async (ssoResult, targetOrgUuid) => {
            const auth = ssoResult || ssoAuth;
            if (!auth) return;
            const tokenUser = {
                access_token: auth.idToken,
                expiration_time: auth.expirationTime,
                refresh_token: auth.refreshToken,
            };
            await dispatch(processLogin(tokenUser));

            let storedUser = {};
            try {
                storedUser = JSON.parse(localStorage.getItem("user") || "{}");
            } catch (e) {
                storedUser = {};
            }
            const orgUuid =
                targetOrgUuid ||
                storedUser.selectedOrganization?.externalUUID ||
                storedUser.availableOrganizations?.[0]?.externalUUID;

            if (orgUuid) {
                await dispatch(selectOrganizationByLogin(orgUuid, API_CONNECTIONS_ROUTE));
            } else {
                history.replace(API_CONNECTIONS_ROUTE);
            }
        },
        [dispatch, ssoAuth]
    );

    // Create the account, then advance to the matching success screen. Throws on failure so the
    // calling step can surface the error and stay put. Email path (activation email, no password)
    // and SSO path (immediate account) are different backend operations. A known SSO identity
    // (existingAccount) logs the user straight in rather than showing the success screen.
    // Fire the registration-conversion tags exactly once per NEW account (GTM + Meta). Reuses the
    // shared implementation — do not rebuild tracking. Not fired for an existing SSO user just
    // logging in (no new account = no account-creation conversion).
    const fireConversionTags = () => {
        fireGTMTagAfterSuccessfulUserRegistration();
        fireMetaTagAfterSuccessfulUserRegistration();
    };

    const register = useCallback(
        async (method, ssoResult) => {
            if (method === "sso") {
                const response = await submitSsoRegistration({ companyName, selectedChannels, ssoAuth: ssoResult, analytics });
                if (response?.requiresConfirmation) {
                    // Existing SSO identity → ask whether to create a new org or just log in.
                    setPromptError(null);
                    setExistingUserPrompt(ssoResult);
                    return response;
                }
                if (response?.existingAccount) {
                    // (Shouldn't hit without confirmation, but handle defensively.)
                    await loginToApp(ssoResult, response.organizationExternalUUID);
                    return response;
                }
                // New SSO account created → conversion + show 4b.
                fireConversionTags();
                setSsoAuth(ssoResult);
                setSignupMethod("sso");
                setStep(REGISTRATION_STEPS.SUCCESS);
                return response;
            }
            const response = await submitEmailRegistration({ companyName, email, selectedChannels, analytics });
            // New email account created → conversion + show 4a.
            fireConversionTags();
            setSignupMethod("email");
            setStep(REGISTRATION_STEPS.SUCCESS);
            return response;
        },
        [companyName, email, selectedChannels, analytics, loginToApp]
    );

    // Prompt "Yes" → create a new org for the existing user, then log them into it.
    // Keep the prompt open with a spinner through login/redirect; a success navigates away and
    // unmounts, so the loading stays visible until the user actually leaves for the app.
    const confirmCreateNewOrg = useCallback(async () => {
        const ssoResult = existingUserPrompt;
        if (!ssoResult) return;
        setPromptError(null);
        setPromptBusy("create");
        try {
            const response = await submitSsoRegistration({
                companyName,
                selectedChannels,
                ssoAuth: ssoResult,
                analytics,
                createNewOrganizationForExistingUser: true,
            });
            // No conversion tag here — it's an existing user (no new account created), just a new org.
            await loginToApp(ssoResult, response?.organizationExternalUUID);
        } catch (err) {
            setPromptError(
                typeof err === "string" && err.trim()
                    ? err
                    : err?.errorMessage || err?.message || "Die Organisation konnte nicht erstellt werden. Bitte versuche es erneut."
            );
            setPromptBusy(null);
        }
    }, [existingUserPrompt, companyName, selectedChannels, analytics, loginToApp]);

    // Prompt "No" → skip org creation, just log the user into their existing account.
    const declineAndLogin = useCallback(async () => {
        const ssoResult = existingUserPrompt;
        if (!ssoResult) return;
        setPromptError(null);
        setPromptBusy("login");
        try {
            await loginToApp(ssoResult); // navigates into the app on success (unmounts the flow)
        } catch (err) {
            setPromptError(
                typeof err === "string" && err.trim()
                    ? err
                    : err?.errorMessage || err?.message || "Die Anmeldung ist fehlgeschlagen. Bitte versuche es erneut."
            );
            setPromptBusy(null);
        }
    }, [existingUserPrompt, loginToApp]);

    const stepContent = useMemo(() => {
        switch (step) {
            case REGISTRATION_STEPS.CHANNELS:
                return (
                    <ChannelSelectionStep
                        selectedChannels={selectedChannels}
                        setSelectedChannels={setSelectedChannels}
                        onNext={() => setStep(REGISTRATION_STEPS.COMPANY)}
                    />
                );
            case REGISTRATION_STEPS.COMPANY:
                return (
                    <CompanyNameStep
                        companyName={companyName}
                        setCompanyName={setCompanyName}
                        onNext={() => setStep(REGISTRATION_STEPS.EMAIL)}
                    />
                );
            case REGISTRATION_STEPS.EMAIL:
                return (
                    <EmailStep
                        email={email}
                        setEmail={setEmail}
                        onEmailSubmit={() => register("email")}
                        onSsoSuccess={(ssoResult) => {
                            if (ssoResult?.email) setEmail(ssoResult.email);
                            return register("sso", ssoResult);
                        }}
                        onMfaRequired={(provider) => {
                            setMfaProvider(provider);
                            setMfaRequired(true);
                        }}
                    />
                );
            case REGISTRATION_STEPS.SUCCESS:
                return (
                    <SuccessStep
                        method={signupMethod}
                        email={email}
                        onGoToAccount={() => loginToApp()}
                        onChangeEmail={() => setStep(REGISTRATION_STEPS.EMAIL)}
                        onResend={() => dispatch(sendActivationLink(email))}
                    />
                );
            default:
                return null;
        }
    }, [step, selectedChannels, companyName, email, signupMethod, register, loginToApp, dispatch]);

    return (
        <div className="registration-screen" data-cy="registration-screen">
            {existingUserPrompt && (
                <ExistingAccountPrompt
                    busy={promptBusy}
                    error={promptError}
                    onConfirm={confirmCreateNewOrg}
                    onDecline={declineAndLogin}
                />
            )}

            {mfaRequired && (
                <MfaPrompt
                    provider={mfaProvider}
                    onResolved={async (ssoResult) => {
                        // Route the 2FA-resolved token through the normal SSO path, then close the prompt.
                        await register("sso", ssoResult);
                        setMfaRequired(false);
                    }}
                    onCancel={() => setMfaRequired(false)}
                />
            )}

            <div className="registration-screen__flow">
                <RegistrationHeader progress={progress} canGoBack={canGoBack} onBack={goBack} />
                <div className="registration-screen__step">
                    <img className="registration-screen__logo" src={pathwayLogo} alt="pathway solutions" />
                    {stepContent}
                </div>
            </div>
            <SocialProofPanel />
        </div>
    );
};

export default RegistrationScreen;
